import { useCallback, useEffect, useRef, useState } from 'react';
import Button from '../ui/Button';
import { ErrorNotice } from '../ui/States';
import { Criterion, Extra, OwnReview, Rating, Rubric, Score, errorMessage } from '../../services/reviewApi';
import { extraAnchor } from './ApplicationView';
import { inputClass } from './parts';

type Values = Record<string, Partial<Score>>;

const RATINGS: Rating[] = ['low', 'medium', 'high'];
const SAVE_DELAY_MS = 1200;

function evidenceFor(c: Criterion, extras: Extra[]) {
  const patterns = (c.evidence ?? []).map((p) => {
    try {
      return new RegExp(p, 'i');
    } catch {
      return null;
    }
  });
  return extras.filter((e) => patterns.some((re) => re?.test(e.label)));
}

function missingOf(rubric: Rubric, values: Values) {
  return rubric.criteria
    .filter((c) => !c.optional)
    .filter((c) => {
      const v = values[c.key];
      if (c.scale === 'score') return v?.score === null || v?.score === undefined;
      if (c.scale === 'text') return !v?.text;
      return !v?.rating;
    })
    .map((c) => c.label);
}

function RatingButtons({
  value,
  disabled,
  onChange,
}: {
  value?: Rating | null;
  disabled: boolean;
  onChange: (r: Rating) => void;
}) {
  return (
    <div className="flex gap-2">
      {RATINGS.map((r) => (
        <button
          key={r}
          type="button"
          disabled={disabled}
          aria-pressed={value === r}
          onClick={() => onChange(r)}
          className={`font-semibold text-small px-3 py-[6px] border ${
            value === r ? 'bg-ink text-paper border-ink' : 'bg-white border-line hover:border-line-strong'
          } disabled:opacity-60`}
        >
          {r}
        </button>
      ))}
    </div>
  );
}

export default function RubricForm({
  rubric,
  review,
  extras,
  editable,
  onSave,
  onSubmit,
}: {
  rubric: Rubric;
  review: OwnReview;
  extras: Extra[];
  editable: boolean;
  onSave: (scores: Values) => Promise<void>;
  onSubmit: (reason?: string) => Promise<void>;
}) {
  const [values, setValues] = useState<Values>(review.scores);
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const dirty = useRef<Set<string>>(new Set());
  const timer = useRef<number | null>(null);
  const latest = useRef(values);
  latest.current = values;

  useEffect(() => {
    setValues(review.scores);
    dirty.current.clear();
  }, [review.id, review.state, review.scores]);

  const flush = useCallback(async () => {
    if (timer.current) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
    if (!dirty.current.size) return;
    const keys = [...dirty.current];
    dirty.current.clear();
    setStatus('saving');
    try {
      await onSave(Object.fromEntries(keys.map((k) => [k, latest.current[k] ?? null])) as Values);
      setStatus('saved');
      setError(null);
    } catch (err) {
      keys.forEach((k) => dirty.current.add(k));
      setStatus('error');
      setError(errorMessage(err, 'Could not save. Your changes are kept on this page; try again.'));
      throw err;
    }
  }, [onSave]);

  const flushRef = useRef(flush);
  flushRef.current = flush;
  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => {
      if (dirty.current.size) e.preventDefault();
    };
    window.addEventListener('beforeunload', warn);
    return () => {
      window.removeEventListener('beforeunload', warn);
      if (timer.current) window.clearTimeout(timer.current);
      flushRef.current().catch(() => undefined);
    };
  }, []);

  const change = (key: string, patch: Partial<Score>) => {
    setValues((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
    dirty.current.add(key);
    setStatus('idle');
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      flush().catch(() => undefined);
    }, SAVE_DELAY_MS);
  };

  const missing = missingOf(rubric, values);
  const reopened = review.state === 'reopened';

  const submit = async () => {
    setSubmitting(true);
    try {
      await flush();
      await onSubmit(reopened ? reason : undefined);
      setError(null);
    } catch (err) {
      setError(errorMessage(err, 'Could not submit the review.'));
    } finally {
      setSubmitting(false);
    }
  };

  let section = '';
  return (
    <div>
      {reopened && review.reopenReason ? (
        <div className="border-l-4 border-accent bg-accent-tint px-[14px] py-3 text-small mb-4">
          Reopened by the chair: {review.reopenReason}
        </div>
      ) : null}
      {rubric.criteria.map((c) => {
        const head = c.section !== section ? c.section : null;
        section = c.section;
        const v = values[c.key] ?? {};
        const evidence = evidenceFor(c, extras);
        return (
          <div key={c.key}>
            {head ? <div className="font-semibold text-small text-ink-2 mt-5 mb-1">{head}</div> : null}
            <div className="border-b border-line py-3">
              <div className="flex items-baseline justify-between gap-3">
                <label className="text-table font-semibold">{c.label}</label>
                {c.optional ? <span className="text-small text-ink-2">optional</span> : null}
              </div>
              {c.guidance ? <p className="text-small text-ink-2 mt-1">{c.guidance}</p> : null}
              {evidence.length ? (
                <div className="text-small mt-1">
                  {evidence.map((e) => (
                    <a key={e.id} href={`#${extraAnchor(e.id)}`} className="link-underline mr-3">
                      {e.label.length > 60 ? `${e.label.slice(0, 60)}…` : e.label}
                    </a>
                  ))}
                </div>
              ) : null}
              <div className="mt-2">
                {c.scale === 'count_lmh' ? (
                  <div className="text-small text-ink-2 mb-2">
                    From the form: <span className="tabular text-ink font-semibold">{c.prefillValue ?? '—'}</span>
                  </div>
                ) : null}
                {c.scale === 'lmh' || c.scale === 'count_lmh' ? (
                  <RatingButtons value={v.rating} disabled={!editable} onChange={(r) => change(c.key, { rating: r })} />
                ) : null}
                {c.scale === 'score' ? (
                  <div className="flex flex-wrap gap-1">
                    {Array.from({ length: (c.max ?? 10) - (c.min ?? 0) + 1 }, (_, i) => (c.min ?? 0) + i).map((n) => (
                      <button
                        key={n}
                        type="button"
                        disabled={!editable}
                        aria-pressed={v.score === n}
                        onClick={() => change(c.key, { score: n })}
                        className={`font-semibold tabular w-9 py-[6px] text-table border ${
                          v.score === n
                            ? 'bg-ink text-paper border-ink'
                            : 'bg-white border-line hover:border-line-strong'
                        } disabled:opacity-60`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                ) : null}
                {c.scale === 'text' ? (
                  <>
                    {c.hint ? <p className="text-small font-semibold mb-1">{c.hint}</p> : null}
                    <textarea
                      className={`${inputClass} h-[120px]`}
                      disabled={!editable}
                      value={v.text ?? ''}
                      onChange={(e) => change(c.key, { text: e.target.value })}
                      onBlur={() => {
                        flush().catch(() => undefined);
                      }}
                    />
                  </>
                ) : null}
              </div>
            </div>
          </div>
        );
      })}

      {error ? (
        <div className="mt-4">
          <ErrorNotice>{error}</ErrorNotice>
        </div>
      ) : null}

      {editable ? (
        <div className="mt-5">
          <div className="text-small text-ink-2 mb-3">
            {status === 'saving'
              ? 'Saving…'
              : status === 'saved'
                ? 'All changes saved'
                : status === 'error'
                  ? 'Not saved'
                  : 'Saves as you go'}
          </div>
          {reopened ? (
            <>
              <label className="block text-small text-ink-2 mb-1">What did you change, and why? (required)</label>
              <textarea
                className={`${inputClass} h-[72px] mb-3`}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </>
          ) : null}
          {missing.length ? <p className="text-small text-ink-2 mb-3">Still to score: {missing.join('; ')}</p> : null}
          <Button
            variant="primary"
            disabled={submitting || missing.length > 0 || (reopened && !reason.trim())}
            onClick={submit}
          >
            {submitting ? 'Submitting…' : reopened ? 'Resubmit review' : 'Submit review'}
          </Button>
          <p className="text-small text-ink-2 mt-2">
            Submitting locks your review. Only the chair can reopen it. Once submitted you will see the other reviewer’s
            name and, when they submit, their scores.
          </p>
        </div>
      ) : null}
    </div>
  );
}
